import type { DatoColor } from '@src/types.ts/story';

/**
 * Formats a DatoCMS color as `rgba(r, g, b, a)`.
 *
 * The single conversion for the whole app: it used to be written out at each
 * call site, and the copies drifted — one skipped the rounding, so the same
 * record produced `rgba(0, 0, 0, 0.35)` applied from the karaoke panel and
 * `rgba(0, 0, 0, 0.34901960784313724)` applied from a video template. Identical
 * on screen, never equal to a comparison.
 *
 * Always built from the components rather than DatoCMS's `cssRgb`, which is CSS
 * Color Level 4 (`rgb(R G B)`) — space-separated, alpha omitted, and not
 * reliably understood by the renderer.
 *
 * DatoCMS reports alpha on 0-255; a value already within 0-1 is taken as
 * normalized. `alphaOverride` (0-1) wins outright, for the caller that derives
 * opacity from a separate `backgroundTransparency` field.
 */
export function datoColorToRgba(
  color: DatoColor,
  alphaOverride?: number | null,
): string {
  const alpha =
    alphaOverride != null
      ? alphaOverride
      : color.alpha > 1
        ? parseFloat((color.alpha / 255).toFixed(2))
        : color.alpha;
  return `rgba(${color.red}, ${color.green}, ${color.blue}, ${alpha})`;
}

/**
 * A color represents the erased / "no color" state when it is absent or fully
 * transparent (alpha 0). Used to keep the color picker and its trigger in sync:
 * an erased value applies transparent rather than null (a null color defaults to
 * black in the renderer), and both the picker and trigger must read that back as
 * "None" instead of black.
 *
 * Recognizes:
 *  - null / undefined / empty string
 *  - the `transparent` keyword
 *  - `rgba(r, g, b, 0)` (alpha 0)
 *  - 8-digit hex with a `00` alpha byte (`#RRGGBB00`)
 */
export function isNoneColor(value: string | null | undefined): boolean {
  if (!value) return true;
  const v = value.toLowerCase().trim();
  if (v === 'transparent') return true;
  const rgba = v.match(/rgba?\(([^)]+)\)/);
  if (rgba) {
    const parts = rgba[1].split(',').map(s => s.trim());
    return parts.length === 4 && parseFloat(parts[3]) === 0;
  }
  if (/^#[0-9a-f]{8}$/.test(v)) return v.slice(7) === '00';
  return false;
}
